import { faChevronDown } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { AccordionDetails, AccordionSummary, Box, Divider, Grid, TextField, Typography } from "@mui/material"
import { DateTimePicker } from "@mui/x-date-pickers"
import { useApiFile } from "auth/useApi2"
import Button from "components/Button"
import CompanyDepartmentSelection from "components/CompanyDepartmentSelection/CompanyDepartmentSelection"
import TextEditor from "components/Fields/TextEditor"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import MultipleFilePickerPreviewList from "components/MultipleFilePickerPreviewList"
import PaperBox from "components/PaperBox"
import { TinyMCE, tinyMceImportDate } from "components/TinyMCE/TinyMCE"
import { Formik } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import moment from "moment"
import { removeQueryFromImgSrc } from "pages/documents/helpers/imageSrcSasKey"
import React, { useContext, useEffect, useRef, useState } from "react"
import * as Yup from "yup"
import { StyledAccordion, StyledBox } from "./ArticleForm.styled"
import { isJson } from "components/TinyMCE/helpers"
import { useTranslation } from "react-i18next"

const articleValidationSchema = (t) =>
  Yup.object().shape({
    subject: Yup.string()
      .required(t("global.title_is_required", "Title is required"))
      .min(2, t("global.name_must_be_atleast_two", "Name needs to be at least 2 characters long")),
    publishedDate: Yup.date()
      .required(t("global.publish_date_is_required", "Publish date is required"))
      .nullable(),
    priorityEndDate: Yup.date().nullable(),
    subTitle: Yup.string().max(
      400,
      t(
        "global.lead_paragraph_may_contain_no_more_than_400",
        "Lead paragraph may contain no more than 400 characters"
      )
    )
  })

const explainPriority =
  "The post will remain at the top of the list until the chosen priority date has passed."

const ArticleForm = ({ customer, editItem, onSubmit, result, feedback }) => {
  const initialValues = editItem
    ? {
        ...editItem
      }
    : {
        subject: "",
        subTitle: "",
        body: "",
        priorityEndDate: null,
        publishedDate: new Date(),
        companyIds: [],
        departmentIds: [],
        attachments: []
      }
  const editMode = editItem ? true : false
  const handleSubmit = async (values) => {
    if (values.body) {
      values.body = removeQueryFromImgSrc(values.body)
    }
    onSubmit(values)
  }
  const [expanded, setExpanded] = React.useState(false)
  const toggleAccordion = () => {
    setExpanded((p) => !p)
  }
  const loadingContext = useContext(GlobalLoaderContext)
  const [fileUploadResult, uploadFile] = useApiFile()
  const [files, setFiles] = useState([])
  const [filesPreview, setFilesPreview] = useState(initialValues.attachments)
  const { t } = useTranslation()

  useEffect(() => {
    if (files) {
      files.map((file) => {
        return uploadFile(apiEndpoints.newsadmin.uploadattachment, file)
      })
    }
  }, [files, uploadFile])

  useEffect(() => {
    if (fileUploadResult.data && fileUploadResult.data.name) {
      setFilesPreview((p) => {
        return [...p, fileUploadResult.data]
      })
    }
  }, [fileUploadResult])

  const onLogoChanged = (value) => {
    setFilesPreview(value)
  }

  useEffect(() => {
    loadingContext.setLoading(() => fileUploadResult.status === 1)
  })

  const [textEditorValue, setTextEditorValue] = useState()
  const formRef = useRef()

  useEffect(() => {
    if (formRef.current && textEditorValue) {
      formRef.current.setFieldValue("body", textEditorValue)
    }
  }, [textEditorValue])

  return (
    <Formik
      innerRef={formRef}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={articleValidationSchema(t)}>
      {({ values, errors, touched, handleChange, handleBlur, isSubmitting, handleSubmit, setFieldValue }) => (
        <form onSubmit={handleSubmit} noValidate>
          <PaperBox>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  autoComplete="off"
                  fullWidth
                  id="subject"
                  label={t("news.heading", "Subject")}
                  value={values.subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.subject && errors.subject ? true : false}
                  helperText={touched.subject && errors.subject ? errors.subject : null}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  multiline={true}
                  minRows={5}
                  autoComplete="off"
                  fullWidth
                  id="subTitle"
                  label={t("news.lead_paragraph", "Lead paragraph")}
                  value={values.subTitle}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.subTitle && errors.subTitle ? true : false}
                  helperText={touched.subTitle && errors.subTitle ? errors.subTitle : null}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                {isJson(initialValues.body) ? (
                  <StyledBox>
                    <TextEditor
                      value={initialValues.body}
                      setData={(e) => {
                        setTextEditorValue(e)
                      }}
                      uploadApi={apiEndpoints.newsadmin.uploadattachment}
                    />
                  </StyledBox>
                ) : (
                  <TinyMCE
                    setFormikData={(e) => {
                      setTextEditorValue(e)
                    }}
                    formikValues={{ body: initialValues.body }}
                    uploadMediaUrl={apiEndpoints.newsadmin.uploadattachment}
                  />
                )}
              </Grid>
            </Grid>
          </PaperBox>
          <Box marginTop={3}>
            <PaperBox>
              <Typography variant="h5">
                {t("global.attach_images", "Attach files from your device")}
              </Typography>
              <Typography mt={1} variant="bodySmall" color="textSecondary">
                {t("news.explain_can_download_files", "Files are available for download in the news post.")}
              </Typography>
              <Box marginTop={3}>
                <MultipleFilePickerPreviewList
                  setFieldValue={setFieldValue}
                  fieldName="attachments"
                  defaultPreviewFiles={filesPreview}
                  setFiles={setFiles}
                  files={files}
                  fileUploadResult={fileUploadResult}
                  onLogoChanged={onLogoChanged}
                  labelInBox={t("global.attach_images", "Attach files from your device")}
                />
              </Box>
            </PaperBox>
          </Box>
          <Box marginTop={3}>
            <PaperBox>
              <Grid container direction="row" justifyContent="space-between" spacing={3}>
                <Grid item>
                  <Typography variant="h5">{t("news.publishing_date", "Publish date")}</Typography>
                  <Typography variant="bodySmall" color="textSecondary">
                    {t("news.explain_publish_chosen_date", "The post will go live on the selected date.")}
                  </Typography>
                </Grid>
                <Grid item>
                  <DateTimePicker
                    value={values.publishedDate ? moment(values.publishedDate) : null}
                    inputFormat="YYYY-MM-DD HH:mm"
                    onChange={(newValue) => {
                      setFieldValue("publishedDate", newValue)
                    }}
                    renderInput={(props) => <TextField {...props} />}
                  />
                </Grid>
              </Grid>
              <Box marginY={3}>
                <Divider light />
              </Box>

              <Grid container direction="row" justifyContent="space-between" spacing={3}>
                <Grid item>
                  <Typography variant="h5">
                    {t("news.prioritize_article", "Prioritize news article")}
                  </Typography>
                  <Typography variant="bodySmall" color="textSecondary">
                    {t("news.explain_priority_expiration", explainPriority)}
                  </Typography>
                </Grid>
                <Grid item>
                  <DateTimePicker
                    value={values.priorityEndDate ? moment(values.priorityEndDate) : null}
                    inputFormat="YYYY-MM-DD HH:mm"
                    onChange={(newValue) => {
                      setFieldValue("priorityEndDate", newValue)
                    }}
                    renderInput={(props) => <TextField {...props} />}
                  />
                </Grid>
              </Grid>
            </PaperBox>
          </Box>
          <Box marginTop={3}>
            <PaperBox>
              <StyledAccordion expanded={expanded} onChange={toggleAccordion}>
                <AccordionSummary
                  expandIcon={<FontAwesomeIcon icon={faChevronDown} />}
                  aria-controls="settings">
                  <Typography variant="h5">{t("global.advanced_settings", "Advanced settings")}</Typography>
                  <Typography variant="bodySmall" color="textSecondary">
                    {t("news.filter_company_department", "Limit article to companies and departments")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid item xs={12}>
                    <CompanyDepartmentSelection
                      companies={customer.companies}
                      initCompanies={initialValues.companyIds}
                      initDepartments={initialValues.departmentIds}
                      onChange={(newState) => {
                        const companyIds = []
                        const departmentIds = []

                        const sortedCompanies = newState.sort((a, b) => a?.name.localeCompare(b?.name))
                        sortedCompanies.forEach((c) => {
                          if (c.selected) companyIds.push(c.id)
                          const sortedDepartments = c.departments.sort((a, b) =>
                            a?.name.localeCompare(b?.name)
                          )
                          sortedDepartments.forEach((d) => {
                            if (d.selected) departmentIds.push(d.id)
                          })
                        })

                        setFieldValue("companyIds", companyIds)
                        setFieldValue("departmentIds", departmentIds)
                      }}
                    />
                  </Grid>
                </AccordionDetails>
              </StyledAccordion>
            </PaperBox>
          </Box>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button color="primary" type="submit" result={result} feedback={feedback}>
              {editMode ? t("global.buttons.save", "Save") : t("news.publish_article", "Publish article")}
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  )
}

export default ArticleForm
